// src/pages/Home.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, Quote, FileText, MessageSquare } from 'lucide-react';

function Home() {
  return (
    <div className="max-w-5xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      {/* SEO metadata */}
      <Helmet>
        <title>Cult of the Red Hat - Democracy Threat Tracker</title>
        <meta name="description" content="Analyzing threats to democracy through fact-checked claims, historical patterns, and data-driven research from established democratic indices." />
        <link rel="canonical" href={window.location.origin} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Cult of the Red Hat - Democracy Threat Tracker",
            "url": window.location.origin,
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${window.location.origin}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
      {/* Hero section */}
      <div className="text-center mb-16">
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
          <span className="block">Cult of the Red Hat</span>
          <span className="block text-slate-600 text-3xl sm:text-4xl mt-3">Democracy Threat Tracker</span>
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-500">
          Analyzing threats to democracy through fact-checked claims, historical patterns, and data-driven research from established democratic indices.
        </p>
      </div>

      {/* Main content cards - single row on desktop, stacked on mobile */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-10 mx-auto">
        {/* Quotes Card */}
        <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 flex flex-col h-full">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4 text-slate-800 mx-auto">
              <Quote size={32} />
            </div>
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-3">Political Claims</h2>
            <p className="text-gray-600 text-center mb-4 flex-grow">
              Explore verified political claims with analysis of their democratic impact and rhetorical tactics.
            </p>
            <Link
              to="/q/"
              className="block w-full bg-slate-800 hover:bg-slate-700 text-white text-center py-3 px-4 rounded-lg font-medium transition-colors duration-200"
            >
              Explore Claims
            </Link>
          </div>
        </div>

        {/* Articles Card */}
        <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 flex flex-col h-full">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4 text-slate-800 mx-auto">
              <BookOpen size={32} />
            </div>
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-3">Articles</h2>
            <p className="text-gray-600 text-center mb-4 flex-grow">
              Read in-depth articles with comprehensive analysis of political narratives and their impact.
            </p>
            <Link
              to="/a/"
              className="block w-full bg-slate-800 hover:bg-slate-700 text-white text-center py-3 px-4 rounded-lg font-medium transition-colors duration-200"
            >
              Browse Articles
            </Link>
          </div>
        </div>
        
        {/* Policies Card */}
        <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 flex flex-col h-full">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4 text-slate-800 mx-auto">
              <FileText size={32} />
            </div>
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-3">Policies</h2>
            <p className="text-gray-600 text-center mb-4 flex-grow">
              Analyze government policies for authoritarian tactics, constitutional issues, and historical precedents.
            </p>
            <Link
              to="/p/"
              className="block w-full bg-slate-800 hover:bg-slate-700 text-white text-center py-3 px-4 rounded-lg font-medium transition-colors duration-200"
            >
              Explore Policies
            </Link>
          </div>
        </div>
        
        {/* Opinions Card */}
        <div className="bg-white overflow-hidden shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-shadow duration-300">
          <div className="p-5 flex flex-col h-full">
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4 text-slate-800 mx-auto">
              <MessageSquare size={32} />
            </div>
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-3">Opinions</h2>
            <p className="text-gray-600 text-center mb-4 flex-grow">
              Read fact-based opinions and analysis on current events and political developments.
            </p>
            <Link
              to="/o/"
              className="block w-full bg-slate-800 hover:bg-slate-700 text-white text-center py-3 px-4 rounded-lg font-medium transition-colors duration-200"
            >
              Read Opinions
            </Link>
          </div>
        </div>
      </div>

      {/* Footer information */}
      <div className="mt-16 border-t border-gray-200 pt-8 text-center text-gray-500 text-sm">
        <p>© {new Date().getFullYear()} Cult of the Red Hat. All rights reserved.</p>
      </div>
    </div>
  );
}

export default Home;